import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Award, Target, Shield, TrendingUp, CheckCircle2 } from 'lucide-react';
import { companyInfo, capabilities } from '../data/mock';

const EnhancedAboutPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24">
      {/* Page Header */}
      <section className="px-8 mb-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="text-sm text-gray-500 uppercase tracking-[0.3em] mb-6 font-semibold">
              About Us
            </div>
            <h1 className="text-6xl md:text-7xl font-bold mb-8 tracking-tight text-white leading-tight">
              Senior leadership.<br />
              Strategic direction.<br />
              Confident delivery.
            </h1>
            <p className="text-2xl text-gray-300 leading-relaxed font-light">
              Independent project leadership delivering major infrastructure and commercial projects across Australia
            </p>
          </motion.div>
        </div>
      </section>

      {/* Leadership Overview */}
      <section className="px-8 mb-32 bg-navy-darker py-32">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="grid md:grid-cols-5 gap-12 items-center mb-16">
              {/* Headshot Placeholder */}
              <div className="md:col-span-2">
                <div className="aspect-square bg-white bg-opacity-5 rounded-sm flex items-center justify-center border border-navy-border">
                  <div className="text-center p-8">
                    <div className="w-40 h-40 bg-white bg-opacity-10 rounded-full mx-auto mb-6"></div>
                    <p className="text-gray-500 text-sm uppercase tracking-wider">
                      Senior Project Director
                    </p>
                  </div>
                </div>
              </div>

              {/* Leadership Info */}
              <div className="md:col-span-3">
                <h2 className="text-4xl font-bold text-white mb-6">
                  {companyInfo.leadership.title}
                </h2>
                <p className="text-xl text-gray-300 leading-relaxed mb-8 font-light">
                  {companyInfo.leadership.experience}
                </p>
                
                <div className="space-y-4">
                  {companyInfo.leadership.expertise.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle2 className="w-5 h-5 text-white mt-1 flex-shrink-0" strokeWidth={2} />
                      <span className="text-gray-300 leading-relaxed">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="px-8 mb-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="text-sm text-gray-500 uppercase tracking-[0.3em] mb-6 font-semibold">
              Core Capabilities
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight text-white">
              Comprehensive Project Leadership
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto font-light leading-relaxed">
              Structured capabilities delivering clarity, control, and confidence across complex project environments
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="space-y-8"
          >
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.id}
                variants={itemVariants}
              >
                <Card className="bg-navy-card border-navy-border hover-lift-enhanced group">
                  <CardContent className="p-10">
                    <div className="grid md:grid-cols-3 gap-8">
                      {/* Capability Header */}
                      <div className="md:col-span-1">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 bg-white bg-opacity-5 rounded-sm flex items-center justify-center text-2xl font-bold text-white group-hover:bg-opacity-10 transition-all">
                            {index + 1}
                          </div>
                        </div>
                        <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-gray-100 transition-colors">
                          {capability.category}
                        </h3>
                        <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">
                          {capability.description}
                        </p>
                      </div>

                      {/* Capability Details */}
                      <div className="md:col-span-2">
                        <h4 className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-4">
                          Key Activities
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {capability.details.map((detail, idx) => (
                            <div key={idx} className="flex items-start gap-3 bg-white bg-opacity-5 p-4 rounded-sm">
                              <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0 mt-0.5" strokeWidth={2} />
                              <span className="text-sm text-gray-300 leading-relaxed">{detail}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Experience Highlights */}
      <section className="px-8 mb-32 bg-navy-darker py-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight text-white">
              Proven Track Record
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto font-light leading-relaxed">
              Delivering results across Australia's major infrastructure and commercial sectors
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '$2.5B+', label: 'Cumulative Project Value' },
              { value: '50+', label: 'Projects Delivered' },
              { value: '6', label: 'Major Sectors' },
              { value: '25+', label: 'Years Experience' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="text-center p-8 bg-white bg-opacity-5 rounded-sm border border-navy-border group cursor-default"
              >
                <div className="text-6xl font-bold text-white mb-4 tracking-tight">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider font-semibold">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values/Approach */}
      <section className="px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white bg-opacity-5 backdrop-blur-sm border border-navy-border rounded-sm p-12 text-center"
          >
            <h3 className="text-3xl font-bold text-white mb-6">
              Our Approach
            </h3>
            <p className="text-xl text-gray-300 leading-relaxed font-light max-w-3xl mx-auto">
              We bring disciplined leadership, structured governance, and strategic clarity to complex projects. 
              Our independent advisory focus ensures client interests remain central throughout delivery, 
              with experienced oversight providing confidence when it matters most.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EnhancedAboutPage;
